/**
 * Bulk Assignment Component
 * Assignment controls for Team Leaders and Admins
 * Extracted from OrdersManagement.jsx
 * 
 * FILTERING LOGIC (Jan 28, 2026):
 * - Tax Only orders → Show only Tax Processors (role: 'processor')
 * - Search Only orders → Show only Search Processors (role: 'search_processor')
 * - All Orders → Show all processors (or filtered by Team Leader role)
 * 
 * TEAM LEADER VISIBILITY:
 * - Tax Team Leader → sees only Tax Processors
 * - Title/Search Team Leader → sees only Search Processors
 */
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { User, UserCheck, X, ChevronDown } from 'lucide-react';
import { Button } from '../../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { isMlsManagerRole } from '../../mls/rollupConfig';
import { SearchableAssigneeSelect } from './SearchableAssigneeSelect';

// Normalize role display names to canonical keys
const DISPLAY_TO_KEY = {
  'Tax Search Team Leader': 'tax_search_team_leader',
  'Title Search Team Leader': 'title_search_team_leader',
  'Tax Processor': 'processor', 'Admin': 'admin', 'Order Desk': 'order_desk',
  'Search Processor': 'search_processor', 'QC Specialist': 'qc_specialist',
};
const nRole = (r) => DISPLAY_TO_KEY[r] || r || '';

/**
 * Normalize processor data for display
 */
const normalizeProcessor = (proc) => {
  if (!proc) return { id: '', label: 'Unknown' };
  return {
    id: proc.id,
    label: proc.full_name || proc.name || proc.email || 'Unknown'
  };
};

/**
 * Filter processors based on order type and user role
 * @param {Array} processors - Full list of processors
 * @param {string} orderTypeFilter - 'all', 'tax', or 'search'
 * @param {Object} user - Current user object with role
 * @returns {Array} Filtered processor list
 */
const filterProcessorsByOrderType = (processors, orderTypeFilter, user) => {
  if (!processors || !Array.isArray(processors)) return [];
  
  // Helper to normalize role for comparison (handles "Tax Processor" display name)
  // ASSIGNEE-MULTIROLE-001: prefer the server's `assignable_workstreams` (covers QC Specialist + Processor
  // multi-role users whose primary role is not a processor); role key is the legacy fallback.
  const normalizeRole = (r) => (r || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const hasWs = (p, ws, roleKeys) => Array.isArray(p?.assignable_workstreams)
    ? p.assignable_workstreams.includes(ws)
    : roleKeys.includes(normalizeRole(p?.role));
  const isTaxProc = (p) => hasWs(p, 'tax_search', ['processor', 'tax_processor']);
  const isSearchProc = (p) => hasWs(p, 'title_search', ['search_processor', 'title_search_processor']);

  // Team Leaders ALWAYS see only their team's processors, regardless of filter
  if (nRole(user?.role) === 'title_search_team_leader') {
    return processors.filter(isSearchProc);
  }
  if (nRole(user?.role) === 'tax_search_team_leader' || nRole(user?.role) === 'team_leader') {
    return processors.filter(isTaxProc);
  }
  
  // For Admin and Order Desk, filter by order type
  if (orderTypeFilter === 'tax' || orderTypeFilter === 'mls') {
    // Tax Only / MLS (Tax Team scope) → Show only Tax Processors
    return processors.filter(isTaxProc);
  }
  if (orderTypeFilter === 'search') {
    // Search Only → Show only Search Processors
    return processors.filter(isSearchProc);
  }
  
  // 'all' or no filter → every Search-stage-eligible processor (tax or title). TSP-ASSIGN-ROLE-001:
  // users with neither workstream (e.g. Typing-only) are never Search-stage assignees.
  return processors.filter(p => isTaxProc(p) || isSearchProc(p));
};

/**
 * Bulk Assignment Controls
 * Shows processor dropdown and assign button for bulk order assignment
 * Handles both Admin and Team Leader selection states
 */
export const BulkAssignmentControls = ({
  user,
  // Selection state - can be Set for admin or team leaders
  selectedOrders,        // Admin selection Set
  setSelectedOrders,     // Admin setter
  selectedOrderIds,      // Team Leader selection Set
  setSelectedOrderIds,   // Team Leader setter
  // Processor lists
  processors,            // Admin processor list (all processors)
  availableProcessors,   // Team Leader processor list (pre-filtered)
  // Assignment state
  bulkAssignee,
  setBulkAssignee,
  onAssign,
  onAssignToMe,
  isBulkAssigning,
  // Optional processor normalizer
  normalizeProcessorFn,
  // Order type filter for processor filtering
  orderTypeFilter = 'all',
  // MLS-ASSIGN-3B: current page orders (used to partition normal vs dual MLS)
  orders = [],
  // MLS-ASSIGN-3B: async handler ({ dualIds, taxSel, mlsSel }) applying
  // component assignments to the selected dual MLS orders
  onApplyMls,
  // TYPING-TL-ASSIGN-001: Typing tab — target list is active Typing Processors only,
  // no self-assign, and onAssign routes to typing assign/reassign/unassign.
  typingMode = false,
  typingProcessors = [],
  // TYPING-PROCESSOR-LIST-001: roster lifecycle ({loading,error,onOpen,onRetry,emptyLabel})
  typingListState = null,
  processorListState = null
}) => {
  // Only show for admin and team leaders
  const canAssign = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role));
  
  // (Early return moved below the hook declarations for rules-of-hooks
  // compliance — see after handleAssignToMe.)

  // Determine which selection state to use based on role
  const isAdmin = nRole(user?.role) === 'admin';
  const selectionSet = isAdmin ? selectedOrders : selectedOrderIds;
  const setSelectionSet = isAdmin ? setSelectedOrders : setSelectedOrderIds;
  const selectedCount = selectionSet?.size || 0;
  
  // Use provided normalizer or default
  const normalize = normalizeProcessorFn || normalizeProcessor;
  
  // Filter processors based on order type AND user role
  // For Admin: use full processors list and filter by orderTypeFilter
  // For Team Leaders: use availableProcessors (pre-filtered by role)
  const processorList = useMemo(() => {
    if (typingMode) return typingProcessors;
    let list;
    if (isAdmin) {
      list = filterProcessorsByOrderType(processors, orderTypeFilter, user);
    } else {
      // Team leaders already have pre-filtered list via availableProcessors
      // But we still apply the filter for extra safety
      list = filterProcessorsByOrderType(availableProcessors, orderTypeFilter, user);
    }
    // Include current user in the dropdown for self-assignment (if not already present)
    if (user && !list.some(p => p.id === user.id)) {
      const currentUserEntry = {
        id: user.id,
        full_name: `${user.full_name || user.name || user.email} (Me)`,
        name: user.full_name || user.name || user.email,
        email: user.email,
        role: user.role
      };
      return [currentUserEntry, ...list];
    }
    return list;
  }, [processors, availableProcessors, orderTypeFilter, user, isAdmin, typingMode, typingProcessors]);

  // Handle "Assign to Me" action
  const handleAssignToMe = useCallback(() => {
    if (onAssignToMe) {
      onAssignToMe();
    } else if (user?.id) {
      setBulkAssignee(user.id);
      // Trigger assign after setting
      setTimeout(() => onAssign?.(), 0);
    }
  }, [user, onAssignToMe, setBulkAssignee, onAssign]);

  // ── MLS-ASSIGN-3B: partition selection into normal vs dual MLS orders ──
  // Component controls (MLS T / MLS M) operate ONLY on dual orders; the
  // existing normal assignee dropdown continues to operate ONLY on normal
  // orders. Three independent assignment targets.
  const selIds = useMemo(() => Array.from(selectionSet || []), [selectionSet]);
  const { dualIds, normalIds } = useMemo(() => {
    const dual = [];
    const normal = [];
    selIds.forEach((id) => {
      const o = (orders || []).find((x) => x.id === id);
      if (o?.components?.tax && o?.components?.mls) dual.push(id);
      else normal.push(id);
    });
    return { dualIds: dual, normalIds: normal };
  }, [selIds, orders]);

  const [mlsTaxSel, setMlsTaxSel] = useState('');   // '' = No change
  const [mlsMlsSel, setMlsMlsSel] = useState('');   // '__unassign__' = explicit unassign
  const [isApplyingMls, setIsApplyingMls] = useState(false);
  useEffect(() => {
    if (dualIds.length === 0) { setMlsTaxSel(''); setMlsMlsSel(''); }
  }, [dualIds.length]);

  // Tax Team members only for component pickers
  const mlsProcessorList = useMemo(() => {
    const isTaxProc = (r) => ['processor', 'tax_processor']
      .includes((r || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_'));
    return (processorList || []).filter((p) => isTaxProc(p.role));
  }, [processorList]);

  const hasDual = dualIds.length > 0;
  const hasNormal = normalIds.length > 0;
  const mlsActionable = mlsTaxSel !== '' || mlsMlsSel !== '';
  const normalActionable = hasNormal && !!bulkAssignee;

  // Unified Apply for selections containing dual MLS orders: applies the
  // normal assignment (if chosen) to normal orders AND component
  // assignments (if chosen) to dual orders. "No change" is a true no-op.
  const handleApply = useCallback(async () => {
    setIsApplyingMls(true);
    try {
      if (normalActionable && onAssign) await onAssign();
      if (mlsActionable && onApplyMls) {
        await onApplyMls({ dualIds, taxSel: mlsTaxSel, mlsSel: mlsMlsSel });
      }
      setMlsTaxSel('');
      setMlsMlsSel('');
      if (setSelectionSet) setSelectionSet(new Set());
      if (setBulkAssignee) setBulkAssignee('');
    } finally {
      setIsApplyingMls(false);
    }
  }, [normalActionable, mlsActionable, onAssign, onApplyMls, dualIds,
      mlsTaxSel, mlsMlsSel, setSelectionSet, setBulkAssignee]);

  // TL-SELF-ASSIGN-001 (MLS): "Assign to Me" for selections containing dual MLS
  // orders. Targets ONLY the chosen workstream(s) via the same component-assign
  // path as the pickers (user_id = me); normal orders in a mixed selection reuse
  // the existing TAX self-assign handler unchanged.
  const canMlsSelfAssign = isMlsManagerRole(user?.role) && !!user?.id;
  const handleMlsAssignToMe = useCallback(async (which) => {
    setIsApplyingMls(true);
    try {
      if (normalIds.length > 0 && onAssignToMe) await onAssignToMe({ quietDual: true });
      if (onApplyMls) {
        await onApplyMls({
          dualIds,
          taxSel: which === 'mls' ? '' : user.id,
          mlsSel: which === 'tax' ? '' : user.id,
        });
      }
      setMlsTaxSel('');
      setMlsMlsSel('');
      if (setSelectionSet) setSelectionSet(new Set());
      if (setBulkAssignee) setBulkAssignee('');
    } finally {
      setIsApplyingMls(false);
    }
  }, [normalIds.length, onAssignToMe, onApplyMls, dualIds, user, setSelectionSet, setBulkAssignee]);

  if (!canAssign) return null;
  
  // Get placeholder text based on filter
  const getPlaceholderText = () => {
    if (typingMode) return 'Select Typing Processor…';
    // Team leaders see their specific team
    if (nRole(user?.role) === 'title_search_team_leader') {
      return 'Select Search Processor...';
    }
    if (nRole(user?.role) === 'tax_search_team_leader' || nRole(user?.role) === 'team_leader') {
      return 'Select Tax Processor...';
    }
    // Admin sees based on order type filter
    if (orderTypeFilter === 'tax') {
      return 'Select Tax Processor...';
    }
    if (orderTypeFilter === 'search') {
      return 'Select Search Processor...';
    }
    return 'Select Assignee...';
  };

  const handleClearSelection = () => {
    if (setSelectionSet) {
      setSelectionSet(new Set());
    }
    if (setBulkAssignee) {
      setBulkAssignee('');
    }
  };

  // ASSIGNEE-SEARCH-001 — pin Unassigned + Me on top; rest sorted A–Z with
  // in-dropdown search (UI-only; emits identical values as the old <select>).
  const meProc = typingMode ? null : processorList?.find(p => p.id === user?.id);
  const meLabel = meProc ? (normalize(meProc).label.includes('(Me)') ? normalize(meProc).label : `${normalize(meProc).label} (Me)`) : null;
  const mainPinned = [
    { value: 'null', label: 'Unassigned' },
    ...(meProc ? [{ value: meProc.id, label: meLabel }] : []),
  ];
  const toOption = (p) => ({ id: p.id, label: normalize(p).label });
  const mainOptions = (processorList || []).filter(p => p.id !== user?.id).map(toOption);
  const mlsOptions = (mlsProcessorList || []).filter(p => p.id !== user?.id).map(toOption);
  const mlsMePinned = ((mlsProcessorList || []).some(p => p.id === user?.id) || canMlsSelfAssign)
    ? [{ value: user.id, label: meLabel || `${user?.full_name || user?.email} (Me)` }] : [];
  const mlsPinned = [
    { value: '', label: 'No change' },
    { value: '__unassign__', label: 'Unassign' },
    ...mlsMePinned,
  ];

  const activeListState = typingMode ? typingListState : processorListState;

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="bulk-assignment-controls">
      {selectedCount > 0 && hasDual && (
        <span className="text-xs font-medium text-gray-600 whitespace-nowrap" data-testid="bulk-selection-summary">
          {selectedCount} Selected · {normalIds.length} Normal · {dualIds.length} MLS
        </span>
      )}

      {(hasNormal || !hasDual) && (
        <SearchableAssigneeSelect
          value={bulkAssignee || ''}
          onChange={setBulkAssignee}
          options={mainOptions}
          pinned={mainPinned}
          placeholder={getPlaceholderText()}
          disabled={selectedCount === 0}
          testId="bulk-assignee-select"
          listState={activeListState}
        />
      )}

      {!hasDual && (
        <Button
          onClick={onAssign}
          disabled={!bulkAssignee || selectedCount === 0 || isBulkAssigning}
          className="flex items-center gap-2"
          title={`${selectedCount} order(s) selected across all pages`}
          data-testid="bulk-assign-button"
        >
          {isBulkAssigning ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Assigning...
            </>
          ) : (
            <>
              <User className="h-4 w-4" />
              Assign ({selectedCount})
            </>
          )}
        </Button>
      )}

      {/* MLS-ASSIGN-3B: component controls — appear ONLY when the selection
          contains dual MLS orders, and operate ONLY on those orders. */}
      {hasDual && (
        <>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <span className="px-1 py-[1px] rounded border text-[9px] font-bold bg-blue-50 text-blue-700 border-blue-200">TAX</span>
              <span className="text-[11px] font-semibold text-gray-700">Tax Search</span>
            </span>
            <SearchableAssigneeSelect
              value={mlsTaxSel}
              onChange={setMlsTaxSel}
              options={mlsOptions}
              pinned={mlsPinned}
              placeholder="No change"
              disabled={isApplyingMls}
              testId="mls-tax-component-select"
              listState={processorListState}
              buttonClassName="text-sm px-2 py-2 rounded border border-indigo-200 bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[150px]"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <span className="px-1 py-[1px] rounded border text-[9px] font-bold bg-emerald-50 text-emerald-700 border-emerald-200">MLS</span>
              <span className="text-[11px] font-semibold text-gray-700">MLS Search</span>
            </span>
            <SearchableAssigneeSelect
              value={mlsMlsSel}
              onChange={setMlsMlsSel}
              options={mlsOptions}
              pinned={mlsPinned}
              placeholder="No change"
              disabled={isApplyingMls}
              testId="mls-mls-component-select"
              listState={processorListState}
              buttonClassName="text-sm px-2 py-2 rounded border border-indigo-200 bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[150px]"
            />
          </div>
          <Button
            onClick={handleApply}
            disabled={(!mlsActionable && !normalActionable) || isApplyingMls || isBulkAssigning}
            className="flex items-center gap-2"
            data-testid="bulk-apply-button"
          >
            {isApplyingMls ? 'Applying…' : 'Apply'}
          </Button>
          {canMlsSelfAssign && !typingMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={selectedCount === 0 || isApplyingMls || isBulkAssigning}
                  className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 disabled:text-gray-400 disabled:border-gray-200"
                  title="Assign the selected orders to yourself — choose which MLS workstream"
                  data-testid="mls-assign-to-me-button"
                >
                  <UserCheck className="h-4 w-4" />
                  Assign to Me
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" data-testid="mls-assign-to-me-menu">
                <DropdownMenuItem onClick={() => handleMlsAssignToMe('tax')} data-testid="mls-assign-to-me-tax">
                  <span className="px-1 py-[1px] mr-2 rounded border text-[9px] font-bold bg-blue-50 text-blue-700 border-blue-200">TAX</span>
                  Tax Search → Me
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleMlsAssignToMe('mls')} data-testid="mls-assign-to-me-mls">
                  <span className="px-1 py-[1px] mr-2 rounded border text-[9px] font-bold bg-emerald-50 text-emerald-700 border-emerald-200">MLS</span>
                  MLS Search → Me
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleMlsAssignToMe('both')} data-testid="mls-assign-to-me-both">
                  <UserCheck className="h-3.5 w-3.5 mr-2" />
                  Both workstreams → Me
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}

      {/* DASH-STABLE-001: always rendered (disabled at 0) so selecting orders never
          reflows the control panel — mirrors the Assign (0) / Set Priority (0) pattern. */}
      {!hasDual && !typingMode && (
        <Button
          variant="outline"
          onClick={handleAssignToMe}
          disabled={selectedCount === 0 || isBulkAssigning}
          className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 disabled:text-gray-400 disabled:border-gray-200"
          title={selectedCount > 0 ? 'Assign selected orders to yourself' : 'Select orders to assign them to yourself'}
          data-testid="assign-to-me-button"
        >
          <UserCheck className="h-4 w-4" />
          Assign to Me
        </Button>
      )}

      <Button
        variant="outline"
        onClick={handleClearSelection}
        disabled={selectedCount === 0}
        className="flex items-center gap-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 border-gray-300 disabled:text-gray-400 disabled:border-gray-200"
        data-testid="clear-selection-button"
      >
        <X className="h-4 w-4" />
        Clear Selection
      </Button>
    </div>
  );
};

/**
 * Quick Assign Dropdown
 * Inline assignment dropdown for individual orders
 */
export const QuickAssignDropdown = ({
  orderId,
  currentAssignee,
  processors,
  onAssign,
  isAssigning,
  disabled,
  normalizeProcessorFn
}) => {
  const normalize = normalizeProcessorFn || normalizeProcessor;

  return (
    <SearchableAssigneeSelect
      value={currentAssignee || ''}
      onChange={(value) => {
        if (value && value !== currentAssignee) onAssign(orderId, value);
      }}
      options={(processors || []).map((p) => ({ id: p.id, label: normalize(p).label }))}
      pinned={[]}
      placeholder="Assign to..."
      disabled={disabled || isAssigning}
      testId={`quick-assign-${orderId}`}
      buttonClassName="text-xs px-2 py-1 rounded border border-gray-300 bg-white w-[140px]"
    />
  );
};

export default BulkAssignmentControls;
